/**
 * Upload Progress Toast Content Component
 * Separated for React Fast Refresh compatibility
 */

import { Upload, CheckCircle, XCircle, FileIcon, RotateCcw } from "lucide-react";
import { UploadBatch, uploadManager } from "../services/uploadManager";

interface UploadProgressContentProps {
  batch: UploadBatch;
  completedFiles: number;
  totalFiles: number;
  avgProgress: number;
  hasErrors: boolean;
  allComplete: boolean;
  currentFileName?: string;
}

export function UploadProgressContent({ 
  batch, 
  completedFiles, 
  totalFiles, 
  avgProgress,
  hasErrors,
  allComplete,
  currentFileName 
}: UploadProgressContentProps) {
  if (allComplete) {
    const successCount = batch.items.filter(i => i.status === "success").length;
    const failedItems = batch.items.filter(i => i.status === "error");
    const errorCount = failedItems.length;

    return (
      <div className="flex items-start gap-3 min-w-[280px]">
        {hasErrors ? (
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/15 flex items-center justify-center">
            <XCircle className="w-5 h-5 text-destructive" />
          </div>
        ) : (
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-foreground">
            {hasErrors ? "Upload completed with errors" : "Upload complete"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {hasErrors
              ? `${successCount} succeeded, ${errorCount} failed`
              : `${totalFiles} file${totalFiles > 1 ? "s" : ""} uploaded successfully`
            }
          </p>

          {hasErrors && (
            <>
              {/* The images that failed — each can be retried (re-sends the same file). */}
              <ul className="mt-2 space-y-1">
                {failedItems.map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <FileIcon className="w-3 h-3 text-destructive flex-shrink-0" />
                      <span className="text-xs text-foreground truncate" title={item.file.name}>
                        {item.file.name}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => uploadManager.retryItem(batch.batchId, item.id)}
                      className="flex items-center gap-1 text-xs font-medium text-primary hover:underline flex-shrink-0"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Retry
                    </button>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => uploadManager.retryFailed(batch.batchId)}
                  className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                >
                  <RotateCcw className="w-3 h-3" />
                  Retry all failed
                </button>
                <button
                  type="button"
                  onClick={() => uploadManager.dismissBatch(batch.batchId)}
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Dismiss
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 min-w-[280px]">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
        <Upload className="w-5 h-5 text-primary animate-pulse" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-sm text-foreground">
            Uploading files
          </p>
          <span className="text-xs font-medium text-primary">
            {completedFiles + 1}/{totalFiles}
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
            style={{ width: `${avgProgress}%` }}
          />
        </div>
        
        {/* Current file name */}
        {currentFileName && (
          <div className="flex items-center gap-1.5 mt-2">
            <FileIcon className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            <p className="text-xs text-muted-foreground truncate">
              {currentFileName.length > 35 
                ? currentFileName.substring(0, 32) + "..." 
                : currentFileName
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
